import type { AxiosInstance } from 'axios';
import type {
    CrmAccessGroup,
    CrmMember,
    MemberAccess,
    MemberAccessPayload,
    PipelineAccessListRes,
    PipelineMembersPayload,
} from '../interfaces/pipelineAccess';
import type { LocalizedLabel } from '../interfaces/opportunity';
import { CRM_ROLES } from '../constants/roles';

interface ManagementMember {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    roles?: { key: string } | Array<{ key: string }>;
}

interface ManagementAccessGroup {
    id: string;
    name: LocalizedLabel | string;
    members?: Array<{ id: string }>;
}

/**
 * Nav: **CRM access** (AQ-11066). Reached as `getCrmRequests().pipelineAccess.<method>()`.
 *
 * The access rows live in `crm_service`; the people and the access groups come from management, through
 * the same authenticated client, whose base URL is the management API. A failed call rejects after the
 * shell's interceptor has toasted it.
 */
export class PipelineAccessApi {
    protected readonly client: AxiosInstance;

    protected readonly baseURL: string;

    constructor(client: AxiosInstance, baseURL: string) {
        this.client = client;
        this.baseURL = baseURL;
    }

    private get accessUrl(): string {
        return `${this.baseURL}/pipeline-access`;
    }

    async list(): Promise<PipelineAccessListRes> {
        const response = await this.client.get<PipelineAccessListRes>(this.accessUrl);
        return response.data;
    }

    async setMember(memberId: string, payload: MemberAccessPayload): Promise<MemberAccess> {
        const response = await this.client.patch<MemberAccess>(`${this.accessUrl}/members/${memberId}`, payload);
        return response.data;
    }

    async updatePipeline(configurationId: string, payload: PipelineMembersPayload): Promise<PipelineAccessListRes> {
        const response = await this.client.patch<PipelineAccessListRes>(
            `${this.accessUrl}/pipelines/${configurationId}`,
            payload,
        );
        return response.data;
    }

    /** Every member of the team who can open the CRM. */
    async listMembers(teamId: string): Promise<CrmMember[]> {
        const response = await this.client.get<{ members: ManagementMember[] }>(`/teams/${teamId}/members`, {
            params: { role: CRM_ROLES.join(','), allTeamMembers: true },
        });
        return response.data.members.map((member) => {
            const role = Array.isArray(member.roles) ? member.roles[0] : member.roles;
            return {
                id: member.id,
                firstName: member.firstName ?? '',
                lastName: member.lastName ?? '',
                email: member.email ?? '',
                role: role?.key ?? '',
            };
        });
    }

    async listAccessGroups(): Promise<CrmAccessGroup[]> {
        // `summary` skips the team, clients and members management would otherwise join into every group.
        const response = await this.client.get<{ accessGroups: ManagementAccessGroup[] }>('/accessgroups', {
            params: { pagination: false, summary: true },
        });
        return (response.data.accessGroups ?? []).map((group) => ({
            id: group.id,
            name: typeof group.name === 'string' ? { en: group.name, fr: group.name } : group.name,
            memberIds: (group.members ?? []).map(({ id }) => String(id)),
        }));
    }
}
